import React, { useState, useRef, useEffect } from 'react';
import { Title } from '../../typography/Title/Title';
import './style.scss';
import { Caption } from '../../typography/Caption/Caption';
interface RulerPickerProps {
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  unit?: 'ft' | 'lbs' | 'cm' | 'kg' | string;
  initialValue?: number;
  stepWidth?: number;
  divisions?: number;
  onUserInteract?: () => void;
  onInteractionEnd?: () => void;
}

const RulerPicker =  (props: RulerPickerProps) => {
  const {
    min,
    max,
    step,
    onChange,
    unit,
    initialValue,
    stepWidth,
    divisions,
    onUserInteract,
    onInteractionEnd
  } = props;
  
  const [value, setValue] = useState(initialValue > 0 ? initialValue : (max + min) / 2);
  const rulerRef = useRef(null);

  const extraSteps = unit === 'ft' ? 36 : 30;
  const extendedMin = min - extraSteps * step;
  const extendedMax = max + extraSteps * step;

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const handleScroll = () => {
    if (rulerRef.current) {
      let scrollLeft = rulerRef.current.scrollLeft;
      let newValue =
        unit === 'ft'
          ? Math.round((scrollLeft / stepWidth) * step * 11) / 11 + extendedMin
          : Math.round(scrollLeft / stepWidth) * step + extendedMin;

      if (newValue < min) {
        newValue = min;
        rulerRef.current.scrollLeft = ((min - extendedMin) / step) * stepWidth;
      }
      if (newValue > max) {
        newValue = max;
        rulerRef.current.scrollLeft = ((max - extendedMin) / step) * stepWidth;
      }

      setValue(newValue);
      onChange && onChange(newValue);
    }
  };

  const handleUserInteraction = () => {
    onUserInteract && onUserInteract();
  };

  const handleInteractionEnd = () => {
    onInteractionEnd && onInteractionEnd();
  };

  useEffect(() => {
    if (rulerRef.current) {
      rulerRef.current.scrollLeft = ((value - extendedMin) / step) * stepWidth;
    }
  }, []);

  return (
    <div className='ruler-container'>
      <Title className={'ruler-value'} >
        {unit === 'ft'
          ? `${Math.floor(value)}' ${Math.round((value - Math.floor(value)) * 12)}"`
          : value}
      </Title>

      <div
        ref={rulerRef}
        onScroll={handleScroll}
        className='ruler-scroll'
        onTouchStart={handleUserInteraction}
        onMouseDown={handleUserInteraction}
        onTouchEnd={handleInteractionEnd}
        onMouseUp={handleInteractionEnd}
      >
        <div className='ruler-track'>
          {Array.from({ length: (extendedMax - extendedMin) / step + 1 }, (_, i) => {
            const currentValue = extendedMin + i * step;
            const isRealValue = currentValue >= min && currentValue <= max;
            const isMajor = i % divisions === 0;

            return (
              <div
                key={i}
                className={
                  unit === 'ft'
                    ? 'ruler-mark_foot'
                    : unit === 'lbs'
                      ? 'ruler-mark_lbs'
                      : 'ruler-mark'
                }
              >
                <div
                  className={`${unit === 'ft' ? 'tick_foot' : unit === 'lbs' ? 'tick_lbs' : 'tick'} 
                  ${isMajor ? 'tick-large' : 'tick-small'} 
                  ${!isRealValue ? 'tick-fake' : ''}`}
                />
                {isMajor && isRealValue && (
                  <Caption className={unit === 'lbs' ? 'ruler-label_lbs' : 'ruler-label'}>
                    {unit === 'ft' || unit === 'lbs' ? Math.floor(currentValue) : currentValue}
                  </Caption>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className={'ruler-pointer'} />
    </div>
  );
};

export default RulerPicker;
